import { supportedLanguages } from '../const';
import { qlubLocalStorage } from '../service/storage/localStorage';

export function detectRTLFromLang(lang?: string) {
    switch (lang) {
        case 'ar':
        case 'fa':
            return true;
        default:
            return false;
    }
}

export function getAvailableLang(lang: string | null | undefined) {
    if (lang) {
        return {
            lang,
        };
    }
    return {};
}

const getBrowserLanguage = (): string | null => {
    if (!process.browser) {
        return null;
    }

    // @ts-ignore
    const browserLang = navigator.language || navigator.userLanguage;
    if (!browserLang) {
        return null;
    }

    // Extract the primary language code (e.g., 'en' from 'en-US')
    const primaryLang = browserLang?.split('-')?.[0];
    if (!primaryLang) {
        return null;
    }

    // Check if the primary language is in the list of supported languages
    if (supportedLanguages.includes(primaryLang)) {
        return primaryLang;
    }

    // Handle some special cases if necessary (e.g., traditional Chinese mapping)
    if (primaryLang === 'zh') {
        if (browserLang.toLowerCase().includes('hk') || browserLang.toLowerCase().includes('hant')) {
            return 'hk'; // Map to Hong Kong variant for Traditional Chinese
        }
        return 'zh'; // Default to Simplified Chinese
    }

    // Fallback language if none match
    return null;
};

const langKey = 'qlub.user.lang';

export const setUserLanguage = (lang: string | null | undefined) => {
    if (!lang) {
        return;
    }
    qlubLocalStorage.set(langKey, lang);
};

export const getUserLanguage = (): string | null | undefined => {
    return qlubLocalStorage.get(langKey);
};

export const getDefaultLanguage = (defaultLang: string | null | undefined) => {
    return getUserLanguage() || getBrowserLanguage() || defaultLang;
};

// Define Unicode ranges for each supported language using two-letter codes
const languageRanges: Record<string, string> = {
    en: '\u0020-\u007E', // English (Basic Latin)
    ar: '\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF', // Arabic
    fa: '\u0600-\u06FF\uFB8A-\uFBFF\u06C0-\u06CE', // Persian (Farsi)
    es: '\u0020-\u007E\u00C0-\u017F', // Spanish (Latin characters with diacritics)
    pt: '\u0020-\u007E\u00C0-\u017F', // Portuguese (Latin characters with diacritics)
    he: '\u0590-\u05FF', // Hebrew
    tr: '\u0020-\u007E\u00C0-\u017F', // Turkish (Latin with specific diacritics)
    ur: '\u0600-\u06FF\u0750-\u077F', // Urdu (overlaps with Arabic/Persian)
    zh: '\u4E00-\u9FFF', // Chinese (Simplified and Traditional)
    ja: '\u3040-\u30FF\u4E00-\u9FFF', // Japanese (Hiragana, Katakana, Kanji)
    ko: '\uAC00-\uD7AF', // Korean (Hangul syllables)
    hk: '\u4E00-\u9FFF', // Hong Kong Chinese (same range as Chinese)
};

// Define punctuation ranges
const punctuationRanges: Record<string, string> = {
    ar: '؟؛', // Arabic punctuation
    fa: '؟،', // Persian punctuation
    es: '¡¿', // Spanish punctuation
    pt: '', // Portuguese uses standard Latin punctuation
};

// Define the emoji range
const emojiRange = '\u1F600-\u1F64F\u1F300-\u1F5FF\u1F680-\u1F6FF\u2600-\u26FF\u2700-\u27BF';

export const isValidText = (
    text: string,
    validLanguages: string[] | undefined,
    allowEmojis: boolean = false,
): boolean => {
    if (!validLanguages?.length) {
        return true;
    }

    // Collect valid ranges for characters and punctuation
    const ranges = validLanguages
        .map((lang) => {
            const charRange = languageRanges[lang.toLowerCase()];
            const punctuation = punctuationRanges[lang.toLowerCase()] || '';
            return charRange ? `${charRange}${punctuation}` : null;
        })
        .filter(Boolean); // Ignore unsupported languages

    // If no valid ranges, return false
    if (ranges.length === 0) {
        return false;
    }

    // Add emoji range if allowed
    if (allowEmojis) {
        ranges.push(emojiRange);
    }

    // Compile regex once based on the ranges
    const regex = new RegExp(`^[${ranges.join('')}0-9.,?!'"؛:()\\[\\]{}\\-—_]+$`);

    // Test the text
    return regex.test(text);
};
